import { colors } from 'components/styles/variables';
import NextLink from 'next/link';
import { useTranslation } from 'react-i18next';
import { EngagementTypes } from 'lib/Tracking/NavBarClick';
import useHashParamFeatureFlag from 'lib/hooks/useHashParamFeatureFlag';
import Dropdown from './Dropdown';
import Item from './Item';
import Link from './Link';
import Bar from './Bar';
import { useLog } from './useNavBarClickLog';

interface LinkConfig {
  href: string;
  engagementType: EngagementTypes;
  i18nKey: string;
  testId: string;
}

export const RESEARCH_LINKS: LinkConfig[] = [
  {
    href: '/guides',
    engagementType: 'guides_and_reviews_tap',
    i18nKey: 'header.guidesAndReviews',
    testId: 'header-guides-link',
  },
  {
    href: '/news-and-analysis',
    engagementType: 'news_and_analysis_tap',
    i18nKey: 'header.newsAndAnalysis',
    testId: 'header-news-analysis-link',
  },
];

export const MORE_LINKS: LinkConfig[] = [
  {
    href: 'https://www.cargurus.com/sell-car/?pid=AutoList',
    engagementType: 'sell_my_car_tap',
    i18nKey: 'header.sellMyCar',
    testId: 'header-sell-car-link',
  },
  {
    href: '/user/settings',
    engagementType: 'settings_tap',
    i18nKey: 'header.settings',
    testId: 'header-settings-link',
  },
];

type TopLevelLink = LinkConfig & { name: string };

// eslint-disable-next-line func-style
export const TOP_LEVEL_LINKS = (isOnlineToOffline: boolean): TopLevelLink[] => [
  {
    name: 'home',
    href: '/',
    engagementType: 'home_tap',
    i18nKey: 'header.home',
    testId: 'header-home-link',
  },
  {
    name: 'search',
    href: '/listings',
    engagementType: 'search_tap',
    i18nKey: 'header.search',
    testId: 'header-search-link',
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ...(isOnlineToOffline
    ? [
        {
          name: 'contacted',
          href: '/noindex/contacted',
          engagementType: 'contacted_tap',
          i18nKey: 'header.contacted',
          testId: 'header-contacted-link',
        },
      ]
    : []),
  {
    name: 'savedSearches',
    href: '/user/saved-searches',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    engagementType: 'saved_searches_tap',
    i18nKey: 'header.savedSearches',
    testId: 'header-saved-searches-link',
  },
  {
    name: 'favorites',
    href: '/user/favorites',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    engagementType: 'favorites_tap',
    i18nKey: 'header.favorites',
    testId: 'header-favorites-link',
  },
];

interface DropdownListProps {
  navItems: LinkConfig[];
}

function DropdownList({ navItems }: DropdownListProps): JSX.Element {
  const { t } = useTranslation();
  const log = useLog();

  return (
    <ul className="navbar-desktop-dropdown-list">
      {navItems.map(({ href, engagementType, i18nKey, testId }) => (
        <li key={href} className="navbar-desktop-dropdown-list-item">
          <NextLink href={href} legacyBehavior prefetch={false}>
            <a
              className="navbar-desktop-dropdown-list-anchor"
              target="_top"
              href={href}
              onClick={(): void => {
                log(engagementType);
              }}
              data-testid={testId}
            >
              {t(i18nKey)}
            </a>
          </NextLink>
        </li>
      ))}
      <style jsx>{`
        .navbar-desktop-dropdown-list {
          top: 63px;
          border-radius: 0 0 8px 8px;
          border: 1px solid ${colors.G1};
          box-shadow: rgba(0, 0, 0, 0.05) 0px 3px 10px 0px;
          background-color: white;
          z-index: 100;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 170px;
          margin: 0;
          padding: 0;
          list-style-type: none;
        }

        .navbar-desktop-dropdown-list-item {
          height: 24px;
          text-align: center;
          margin-bottom: 25px;
          margin-top: 25px;
        }

        .navbar-desktop-dropdown-list-anchor {
          color: ${colors.G4};
          font-weight: 300;
          text-decoration: none;
          width: 113px;
          font-size: 16px;
          height: 63px;
        }

        a:hover {
          color: ${colors.ALR1};
        }
      `}</style>
    </ul>
  );
}

interface DesktopProps {
  currentItem?: string;
  className?: string;
}

function Desktop({ currentItem, className }: DesktopProps): JSX.Element {
  const { t } = useTranslation();
  const isOnlineToOffline = useHashParamFeatureFlag('isOnlineToOffline');
  return (
    <nav className={`${className}`}>
      <ul className="navbar-desktop-list">
        {TOP_LEVEL_LINKS(isOnlineToOffline).map(
          ({ name, href, engagementType, i18nKey, testId }) => (
            <Item key={href} isActive={name === currentItem}>
              {({ isActive }): JSX.Element => (
                <>
                  <Link
                    href={href}
                    isActive={isActive}
                    engagementType={engagementType}
                    data-testid={testId}
                  >
                    {t(i18nKey)}
                  </Link>
                  <Bar isActive={isActive} />
                </>
              )}
            </Item>
          )
        )}
        <Dropdown text={t('header.research')}>
          <DropdownList navItems={RESEARCH_LINKS} />
        </Dropdown>
        <Dropdown text={t('header.more')}>
          <DropdownList navItems={MORE_LINKS} />
        </Dropdown>
      </ul>
      <style jsx>
        {`
          .navbar-desktop-list {
            margin: 0;
            padding: 0;
            list-style-type: none;
            display: flex;
          }

          nav {
            text-align: right;
            height: 63px;
          }
        `}
      </style>
    </nav>
  );
}

export default Desktop;
